import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SHOP_ENABLED } from "@/lib/config";

const NAV = [
  ...(SHOP_ENABLED ? [{ label: "Shop", to: "/shop" }] : []),
  { label: "Where We Sell", hash: "sell" },
  ...(SHOP_ENABLED ? [{ label: "Collections", hash: "collections" }] : []),
  { label: "Consign", to: "/consign" },
];

export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -70 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
}

export function useMPNav() {
  const navigate = useNavigate();
  const location = useLocation();
  return (item) => {
    if (item.to) {
      navigate(item.to);
    } else if (item.hash) {
      if (location.pathname === "/") scrollToId(item.hash);
      else navigate({ pathname: "/", hash: item.hash });
    }
  };
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const go = useMPNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || location.pathname !== "/";

  const handle = (item) => {
    setOpen(false);
    go(item);
  };

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-300 ${
          solid
            ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 h-[68px] flex items-center justify-between">
          <button
            data-testid="logo-home"
            onClick={() => navigate("/")}
            className="flex items-center group"
            aria-label="Monthly Pops home"
          >
            <img
              src="/brand/mp-wordmark-white.png"
              alt="Monthly Pops"
              className="h-5 md:h-[26px] w-auto transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((n) => (
              <button
                key={n.label}
                data-testid={`nav-${n.label.toLowerCase().split(" ")[0]}`}
                onClick={() => handle(n)}
                className="text-[13px] uppercase tracking-[0.18em] font-medium text-white/70 hover:text-[#3FE1D1] transition-colors duration-200"
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="hidden md:block">
            <button
              data-testid="header-consign-cta"
              onClick={() => navigate("/consign")}
              className="bg-[#3FE1D1] text-[#050505] font-bold uppercase text-xs tracking-[0.16em] px-6 py-3 hover:bg-white transition-colors duration-200"
            >
              Consign With Us
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-[68px] left-0 right-0 z-[99] md:hidden bg-[#050505]/95 backdrop-blur-xl border-b border-white/10 px-6 py-6 flex flex-col gap-5"
          >
            {NAV.map((n) => (
              <button
                key={n.label}
                onClick={() => handle(n)}
                className="text-left text-lg font-display uppercase tracking-wide text-white/80"
              >
                {n.label}
              </button>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                navigate("/consign");
              }}
              className="bg-[#3FE1D1] text-[#050505] font-bold uppercase text-sm tracking-widest px-6 py-3 mt-2"
            >
              Consign With Us
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
